import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BarChart2, Shield, Smartphone, DollarSign, Check, Zap } from 'react-feather';
import Footer from '../../components/Footer';

export default function ArmaTuEvento() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      title: "Control Total",
      desc: "Gestiona aforo, listas de invitados y precios en tiempo real desde tu celular.",
      icon: <Smartphone className="text-white" size={24} />,
      col: "md:col-span-1"
    },
    {
      title: "Data que Vende",
      desc: "Conoce quiénes son tus mejores clientes. Edad, género y frecuencia de asistencia para mejorar tu marketing.",
      icon: <BarChart2 className="text-white" size={24} />,
      col: "md:col-span-2" // Tarjeta ancha
    },
    {
      title: "Pagos Rápidos",
      desc: "Recibe la liquidación de tus entradas directamente en tu cuenta bancaria en 48h hábiles.",
      icon: <DollarSign className="text-white" size={24} />,
      col: "md:col-span-1"
    },
    {
      title: "Seguridad Anti-Fraude",
      desc: "Nuestros QRs dinámicos cambian cada 30 segundos. Imposible de falsificar con capturas de pantalla.",
      icon: <Shield className="text-white" size={24} />,
      col: "md:col-span-2" // Tarjeta ancha
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-white selection:text-black overflow-x-hidden">
      
      {/* 1. TEXTURA DE RUIDO (Noise) */}
      <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.04] mix-blend-overlay" 
           style={{ backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")' }}>
      </div>

      {/* 2. BACKGROUND ORBS (Sutil verde/cian para dinero/éxito) */}
      <div className="fixed top-[-10%] right-[-10%] w-[600px] h-[600px] bg-emerald-900/10 rounded-full blur-[120px] pointer-events-none" />

      {/* HEADER NAV */}
      <div className="fixed top-0 left-0 w-full z-40 px-6 py-6 flex justify-between items-center pointer-events-none">
        <button 
            onClick={() => navigate(-1)} 
            className="pointer-events-auto flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest text-white/50 hover:text-white transition-colors group uppercase bg-black/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/5"
        >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Volver
        </button>
      </div>

      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* --- HERO SECTION --- */}
          <motion.div 
            className="mb-24 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs font-bold text-emerald-400 mb-8 uppercase tracking-widest">
                <Zap size={12} fill="currentColor" />
                Para Organizadores
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tighter text-white leading-[0.9]">
              CREA. GESTIONA. <br/> <span className="text-white/20">ESCALA.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto font-light leading-relaxed mb-10">
              Deja de usar listas de papel y Excel. Pásate a la plataforma que usan los eventos más exclusivos de Lima.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                   href="https://wa.me/51941549341?text=Hola,%20quiero%20organizar%20un%20evento%20con%20GoClub" 
                   target="_blank"
                   rel="noreferrer"
                   className="px-8 py-4 bg-white text-black font-bold rounded-full hover:scale-105 transition-transform shadow-[0_0_30px_-10px_rgba(255,255,255,0.3)]"
                >
                    EMPEZAR AHORA
                </a>
                <span className="text-sm text-white/30 px-4">o contáctanos por DM</span>
            </div>
          </motion.div>

          {/* --- TICKER (MARQUEE) --- */}
          <div className="w-full overflow-hidden border-y border-white/10 py-4 mb-24 bg-white/5 relative">
             <div className="whitespace-nowrap animate-marquee flex gap-12 text-white/20 font-bold text-xl uppercase tracking-widest select-none">
                <span>Sold Out Events</span>
                <span>•</span>
                <span>Real Time Analytics</span>
                <span>•</span>
                <span>Secure Access</span>
                <span>•</span>
                <span>Fast Payouts</span>
                <span>•</span>
                <span>Sold Out Events</span>
                <span>•</span>
                <span>Real Time Analytics</span>
                <span>•</span>
                <span>Secure Access</span>
             </div>
          </div>

          {/* --- BENTO GRID BENEFITS --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-24">
            {benefits.map((item, i) => (
                <motion.div 
                    key={i}
                    className={`${item.col} group relative bg-[#0A0A0A] border border-white/10 p-8 rounded-2xl hover:border-white/30 transition-all duration-300 hover:-translate-y-1`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                    <div className="absolute top-8 right-8 p-3 bg-white/5 rounded-full group-hover:bg-white/10 transition-colors">
                        {item.icon}
                    </div>
                    <div className="mt-16 md:mt-24">
                        <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                        <p className="text-white/50 leading-relaxed max-w-sm">
                            {item.desc}
                        </p>
                    </div>
                </motion.div>
            ))}
          </div>

          {/* --- COMPARISON TABLE --- */}
          <motion.div 
            className="mb-24 bg-white/[0.02] border border-white/10 rounded-3xl p-8 md:p-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-12 text-center">La diferencia AFTR</h2>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/10">
                            <th className="py-4 pl-4 text-white/30 font-normal uppercase text-sm tracking-widest">Característica</th>
                            <th className="py-4 text-center text-white/50 font-normal">Otros</th>
                            <th className="py-4 text-center text-white font-bold text-lg bg-white/5 rounded-t-xl min-w-[150px]">AFTR</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm md:text-base">
                        {[
                            { feat: "Seguridad QR", bad: "Estáticos (Captura de pantalla)", good: "Dinámicos (Anti-fraude)" },
                            { feat: "Liquidación", bad: "7-15 días", good: "48 horas" },
                            { feat: "Data", bad: "Básica", good: "Analytics Avanzados" },
                            { feat: "Soporte", bad: "Email (Lento)", good: "WhatsApp Directo" },
                        ].map((row, i) => (
                            <tr key={i} className="border-b border-white/5 last:border-0">
                                <td className="py-6 pl-4 font-medium text-white/70">{row.feat}</td>
                                <td className="py-6 text-center text-white/30">{row.bad}</td>
                                <td className="py-6 text-center text-white font-bold bg-white/5">{row.good}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
          </motion.div>

          {/* --- FINAL CTA --- */}
          <div className="text-center max-w-2xl mx-auto">
             <h2 className="text-4xl font-bold mb-6">¿Listo para llenar tu local?</h2>
             <p className="text-white/50 mb-8">
                Únete a la red de organizadores que están modernizando la noche en Lima.
             </p>
             <Link
                to="/support" // O link a WhatsApp directo
                className="inline-block px-12 py-5 bg-white text-black font-bold rounded-full hover:scale-105 transition-transform"
             >
                HABLAR CON VENTAS
             </Link>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}